import { useState, useCallback, useEffect, useMemo } from "react";
import { Link } from "react-router";
import { motion, LayoutGroup } from "framer-motion";
import {
  Shuffle,
  ChevronDown,
  Star,
  Clock,
  Layers,
  Grid3X3,
  Download,
  Volume2,
  TrendingUp,
  Gem,
  Play,
  Wrench,
  HelpCircle,
  BarChart3,
  X,
  Keyboard,
  Zap,
  Globe,
  Music,
  Copy,
  Check,
  Tag,
  ChevronRight,
  Trophy,
  Users,
  Smartphone,
  Monitor,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import type { SoundResponse, FeaturedPick, PublicStatsResponse, ComboResponse } from "@wizard-sounds/shared";
import { SORT_OPTIONS } from "@wizard-sounds/shared";
import { useAudio } from "@/components/audio-provider";
import { NowPlayingBar } from "@/components/now-playing-bar";
import { HeaderQuote } from "@/components/header-quote";
import { PlaybackPanel } from "@/components/playback-panel";
import { SoundCard } from "@/components/sound-card";
import { ComboCard } from "@/components/combo-card";
import { ComboBuilderModal } from "@/components/combo-builder-modal";
import { SearchBar } from "@/components/search-bar";
import { useSounds } from "@/hooks/use-sounds";
import { useFavorites } from "@/hooks/use-favorites";
import { useKeyboard } from "@/hooks/use-keyboard";
import { usePWA } from "@/hooks/use-pwa";
import { cn } from "@/lib/utils";
import { useTimezone, timezoneLabel } from "@/lib/timezone";
import { api } from "@/lib/api";
import { useScrollLock } from "@/lib/use-scroll-lock";

function HelpModal({ onClose }: { onClose: () => void }) {
  useScrollLock();
  const [copied, setCopied] = useState<string | null>(null);
  const origin = window.location.origin;

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };

  const CopyButton = ({ text, label }: { text: string; label: string }) => (
    <button onClick={() => handleCopy(text, label)} className="shrink-0 rounded-lg p-1 text-fog hover:text-parchment">
      {copied === label ? <Check className="h-3.5 w-3.5 text-moss" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/60 p-2 backdrop-blur-sm sm:py-8 sm:px-4">
      <div className="flex w-full max-w-lg flex-col rounded-[18px] border border-mist bg-slate shadow-xl max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-4rem)]">
        <div className="flex shrink-0 items-center justify-between border-b border-mist px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-spark/15">
              <HelpCircle className="h-4 w-4 text-spark" />
            </div>
            <h2 className="font-display text-lg font-bold tracking-display text-parchment">Help</h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-fog hover:bg-slate-raised">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <section>
            <h3 className="flex items-center gap-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              <Keyboard className="h-3.5 w-3.5" /> Keyboard Shortcuts
            </h3>
            <div className="mt-2 space-y-1.5">
              {[
                { key: "Space", action: "Stop all sounds" },
                { key: "1–9", action: "Quick play sounds by position" },
                { key: "/", action: "Focus search bar" },
              ].map(({ key, action }) => (
                <div key={key} className="flex items-center justify-between rounded-xl bg-ink/50 px-3 py-2">
                  <span className="text-sm text-parchment">{action}</span>
                  <kbd className="rounded-lg bg-mist px-2 py-0.5 font-mono text-[11px] text-fog">{key}</kbd>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="flex items-center gap-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              <Zap className="h-3.5 w-3.5" /> Integrations
            </h3>
            <p className="mt-1.5 text-sm text-fog">Use trigger URLs with Stream Deck, OBS browser sources, or any automation tool. The sound plays automatically when the URL is opened.</p>
            <div className="mt-2 space-y-1.5">
              <div className="flex items-center gap-2 rounded-xl bg-ink/50 px-3 py-2">
                <Play className="h-3.5 w-3.5 shrink-0 text-fog" />
                <code className="min-w-0 flex-1 truncate font-mono text-[11px] text-spark">{origin}/trigger/:slug</code>
              </div>
              <p className="px-1 text-[11px] text-fog">Add <code className="font-mono text-spark">?close=1</code> to auto-close the tab when the sound finishes.</p>
            </div>
          </section>

          <section>
            <h3 className="flex items-center gap-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              <Globe className="h-3.5 w-3.5" /> Public API
            </h3>
            <p className="mt-1.5 text-sm text-fog">All endpoints return JSON. No authentication required.</p>
            <div className="mt-2 space-y-1.5">
              {[
                { method: "GET", path: "/api/sounds", desc: "List sounds (query: q, tags, sort, limit, cursor)" },
                { method: "GET", path: "/api/sounds/:slug", desc: "Get a single sound by slug" },
                { method: "POST", path: "/api/sounds/:id/play", desc: "Record a play event" },
                { method: "GET", path: "/api/tags", desc: "List all tags with sound counts" },
                { method: "GET", path: "/api/featured", desc: "Get featured picks (daily, trending, gem)" },
                { method: "GET", path: "/api/trending", desc: "Get trending sounds (7-day window)" },
                { method: "GET", path: "/api/boards", desc: "Get board tabs with sounds" },
                { method: "GET", path: "/api/search/suggest", desc: "Search autocomplete (query: q)" },
              ].map(({ method, path, desc }) => (
                <div key={path} className="group rounded-xl bg-ink/50 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className={cn("rounded px-1.5 py-0.5 font-mono text-[9px] font-bold", method === "POST" ? "bg-moss/15 text-moss" : "bg-spark/15 text-spark")}>{method}</span>
                    <code className="min-w-0 flex-1 truncate font-mono text-[11px] text-parchment">{path}</code>
                    <CopyButton text={`${origin}${path}`} label={path} />
                  </div>
                  <p className="mt-1 text-[11px] text-fog">{desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="flex items-center gap-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              <Music className="h-3.5 w-3.5" /> Pages
            </h3>
            <div className="mt-2 space-y-1.5">
              {[
                { path: "/", desc: "Main soundboard with search, sort, and filters" },
                { path: "/s/:slug", desc: "Sound detail with waveform, transcript, and share links" },
                { path: "/board", desc: "Streamer board mode — grid layout optimized for live use" },
                { path: "/trigger/:slug", desc: "Auto-play page for integrations" },
                { path: "/admin", desc: "Admin panel (requires Cloudflare Access)" },
              ].map(({ path, desc }) => (
                <div key={path} className="flex items-start gap-2 rounded-xl bg-ink/50 px-3 py-2">
                  <code className="shrink-0 font-mono text-[11px] text-spark">{path}</code>
                  <span className="text-[11px] text-fog">{desc}</span>
                </div>
              ))}
            </div>
          </section>

          <div className="text-center font-mono text-[10px] text-fog/50">
            v{__APP_VERSION__}
          </div>
        </div>
      </div>
    </div>
  );
}

const COUNTRY_NAMES: Record<string, string> = {
  US: "United States", GB: "United Kingdom", CA: "Canada", AU: "Australia",
  DE: "Germany", FR: "France", JP: "Japan", BR: "Brazil", IN: "India",
  MX: "Mexico", NL: "Netherlands", SE: "Sweden", ES: "Spain", IT: "Italy",
  KR: "South Korea", CN: "China", NZ: "New Zealand", IE: "Ireland",
  SG: "Singapore", PH: "Philippines", TH: "Thailand",
};

const tooltipStyle = {
  backgroundColor: "#11172a",
  border: "1px solid #252e47",
  borderRadius: "12px",
  fontSize: 12,
  fontFamily: "var(--font-mono)",
  color: "#f5f0e6",
};

function StatsModal({ onClose }: { onClose: () => void }) {
  useScrollLock();
  const [data, setData] = useState<PublicStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const { tz, toggleUtc } = useTimezone();

  useEffect(() => {
    api.stats().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/60 p-2 backdrop-blur-sm sm:py-8 sm:px-4" onClick={onClose}>
      <div className="flex w-full max-w-lg flex-col rounded-[18px] border border-mist bg-slate shadow-xl max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-4rem)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex shrink-0 items-center justify-between border-b border-mist px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-spark/15">
              <BarChart3 className="h-4 w-4 text-spark" />
            </div>
            <h2 className="font-display text-lg font-bold tracking-display text-parchment">Stats</h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-fog hover:bg-slate-raised">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-spark border-t-transparent" />
          </div>
        ) : data ? (
          <div className="space-y-4 px-6 py-5">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {[
                { label: "Total Plays", value: formatStat(data.total_plays), icon: <Play className="h-3.5 w-3.5" /> },
                { label: "Sounds", value: String(data.total_sounds), icon: <Music className="h-3.5 w-3.5" /> },
                { label: "Combos", value: String(data.total_combos), icon: <Layers className="h-3.5 w-3.5" /> },
                { label: "Today", value: formatStat(data.plays_today), icon: <TrendingUp className="h-3.5 w-3.5" /> },
                { label: "This Week", value: formatStat(data.plays_this_week), icon: <Zap className="h-3.5 w-3.5" /> },
                { label: "Listeners (30d)", value: formatStat(data.unique_listeners_30d), icon: <Users className="h-3.5 w-3.5" /> },
              ].map(({ label, value, icon }) => (
                <div key={label} className="rounded-xl border border-spark/20 bg-spark/5 p-3">
                  <div className="flex items-center gap-1 text-fog">{icon}<span className="font-mono text-[9px] uppercase tracking-wider">{label}</span></div>
                  <p className="mt-1 font-display text-xl font-bold tracking-display text-parchment">{value}</p>
                </div>
              ))}
            </div>

            {data.plays_per_day.length > 0 && (
              <div className="rounded-xl border border-mist bg-ink/30 p-3">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[9px] font-medium uppercase tracking-wider text-fog">Plays · 30 days</p>
                  <button
                    onClick={toggleUtc}
                    className="rounded-md border border-mist/50 px-1.5 py-0.5 font-mono text-[9px] text-fog transition-colors hover:border-spark/50 hover:text-parchment"
                  >
                    {timezoneLabel(tz)}
                  </button>
                </div>
                <div className="mt-2 h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.plays_per_day}>
                      <defs>
                        <linearGradient id="statsGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#ffb649" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#ffb649" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#252e47" vertical={false} />
                      <XAxis dataKey="date" stroke="#8b95a8" fontSize={9} fontFamily="var(--font-mono)" tickFormatter={(d: string) => d.slice(5)} tickLine={false} axisLine={false} />
                      <YAxis stroke="#8b95a8" fontSize={9} fontFamily="var(--font-mono)" tickLine={false} axisLine={false} width={25} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Area type="monotone" dataKey="count" stroke="#ffb649" strokeWidth={2} fill="url(#statsGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {data.top_sounds.length > 0 && (
              <div>
                <p className="flex items-center gap-1.5 font-mono text-[9px] font-medium uppercase tracking-wider text-fog">
                  <Trophy className="h-3 w-3" /> Top sounds
                </p>
                <div className="mt-2 space-y-1">
                  {data.top_sounds.map((s, i) => (
                    <div key={s.slug} className="flex items-center gap-2.5 rounded-xl bg-ink/30 px-3 py-2">
                      <span className="font-mono text-[10px] text-fog">{i + 1}</span>
                      <span className="min-w-0 flex-1 truncate text-sm text-parchment">{s.title}</span>
                      <span className="shrink-0 font-mono text-[10px] text-fog">{formatStat(s.play_count)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.top_combo && (
              <div>
                <p className="flex items-center gap-1.5 font-mono text-[9px] font-medium uppercase tracking-wider text-fog">
                  <Layers className="h-3 w-3" /> Most popular combo
                </p>
                <Link
                  to={`/c/${data.top_combo.slug}`}
                  className="mt-2 flex items-center gap-2.5 rounded-xl border border-spark/20 bg-spark/5 px-3 py-2.5 transition-colors hover:bg-spark/10"
                >
                  <Trophy className="h-4 w-4 text-spark" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-parchment">{data.top_combo.name}</span>
                  <span className="shrink-0 font-mono text-[10px] text-fog">{formatStat(data.top_combo.play_count)} plays</span>
                </Link>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {data.top_countries.length > 0 && (
                <div>
                  <p className="flex items-center gap-1.5 font-mono text-[9px] font-medium uppercase tracking-wider text-fog">
                    <Globe className="h-3 w-3" /> Top countries
                  </p>
                  <div className="mt-2 space-y-1">
                    {data.top_countries.map((c) => (
                      <div key={c.country} className="flex items-center justify-between rounded-lg bg-ink/30 px-2.5 py-1.5">
                        <span className="text-xs text-parchment">{COUNTRY_NAMES[c.country] ?? c.country}</span>
                        <span className="font-mono text-[9px] text-fog">{formatStat(c.count)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data.devices.length > 0 && (
                <div>
                  <p className="flex items-center gap-1.5 font-mono text-[9px] font-medium uppercase tracking-wider text-fog">
                    <Smartphone className="h-3 w-3" /> Devices
                  </p>
                  <div className="mt-2 space-y-1">
                    {data.devices.map((d) => {
                      const total = data.devices.reduce((s, x) => s + x.count, 0);
                      const pct = total > 0 ? Math.round((d.count / total) * 100) : 0;
                      return (
                        <div key={d.device_type} className="flex items-center gap-2 rounded-lg bg-ink/30 px-2.5 py-1.5">
                          {d.device_type === "mobile" ? <Smartphone className="h-3 w-3 text-spark" /> : <Monitor className="h-3 w-3 text-fog" />}
                          <span className="flex-1 text-xs capitalize text-parchment">{d.device_type}</span>
                          <span className="font-mono text-[9px] text-fog">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

          </div>
        ) : (
          <div className="py-12 text-center text-sm text-fog">Failed to load stats</div>
        )}
        </div>
      </div>
    </div>
  );
}

function formatStat(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function Soundboard() {
  const {
    play,
    stopAll,
    playbackRate,
  } = useAudio();
  const {
    sounds,
    tags,
    loading,
    total,
    cursor,
    sort,
    query,
    debouncedQuery,
    selectedTags,
    setSort,
    setQuery,
    toggleTag,
    loadMore,
    incrementPlayCount,
  } = useSounds();
  const { isFavorite, toggleFavorite, isComboFavorite, toggleComboFavorite, addRecent, favorites, recents } =
    useFavorites();
  const { canInstall, install } = usePWA();
  const [picks, setPicks] = useState<FeaturedPick[]>([]);
  const [filter, setFilter] = useState<"all" | "favorites" | "recent" | "combos">("all");
  const [showHelp, setShowHelp] = useState(false);
  const [showPlayback, setShowPlayback] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [combos, setCombos] = useState<ComboResponse[]>([]);
  const [comboLimitRemaining, setComboLimitRemaining] = useState(100);
  const [showComboBuilder, setShowComboBuilder] = useState(false);
  const PAGE_SIZE = 24;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    api.featured().then((res) => setPicks(res.picks ?? [])).catch(() => {});
  }, []);

  const fetchCombos = useCallback(() => {
    api.combos.list({ sort: "popular" }).then((res) => {
      setCombos(res.combos);
      setComboLimitRemaining(res.limit_remaining);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchCombos();
  }, [fetchCombos]);

  // Reset visible count when search/sort/filter changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [debouncedQuery, sort, selectedTags.join(","), filter]);

  const handlePlay = useCallback(
    (sound: SoundResponse) => {
      play(sound.id, sound.audio_url, {
        title: sound.title,
        durationMs: sound.duration_ms,
        slug: sound.slug,
      });
      addRecent(sound.id);
      incrementPlayCount(sound.id);
    },
    [play, addRecent, incrementPlayCount],
  );

  // Optimistic play count for combos
  const handleComboPlayed = useCallback((comboId: string) => {
    setCombos((prev) =>
      prev.map((c) =>
        c.id === comboId ? { ...c, play_count: c.play_count + 1 } : c,
      ),
    );
  }, []);

  const handleRandom = useCallback(() => {
    api.sounds.random().then((sound) => {
      play(sound.id, sound.audio_url, {
        title: sound.title,
        durationMs: sound.duration_ms,
        slug: sound.slug,
      });
      addRecent(sound.id);
      incrementPlayCount(sound.id);
    }).catch(() => {
      // Fallback to loaded sounds if random endpoint fails
      if (sounds.length > 0) {
        const randomSound = sounds[Math.floor(Math.random() * sounds.length)];
        handlePlay(randomSound);
      }
    });
  }, [play, addRecent, incrementPlayCount, sounds, handlePlay]);

  useKeyboard(sounds, handlePlay, stopAll);

  // Client-side filter for combos matching the selected tags + search query,
  // mirroring the sounds API (a combo matches if it has ANY selected tag).
  // Strip punctuation so "I cant" matches "I can't" etc.
  const filteredCombos = useMemo(() => {
    let result = combos;
    if (selectedTags.length > 0) {
      result = result.filter((c) => c.tags.some((t) => selectedTags.includes(t.slug)));
    }
    if (!debouncedQuery) return result;
    const strip = (s: string) => s.replace(/[''`.,!?;:"-]/g, "").toLowerCase();
    const q = strip(debouncedQuery);
    return result.filter(
      (c) =>
        strip(c.name).includes(q) ||
        c.sounds.some((s) => strip(s.title).includes(q)),
    );
  }, [combos, debouncedQuery, selectedTags]);

  // Unified feed: merge sounds + combos sorted together
  type FeedItem =
    | { type: "sound"; data: SoundResponse }
    | { type: "combo"; data: ComboResponse };

  const feed = useMemo<FeedItem[]>(() => {
    if (filter === "favorites") {
      return sounds.filter((s) => favorites.has(s.id)).map((s) => ({ type: "sound" as const, data: s }));
    }
    if (filter === "recent") {
      return sounds.filter((s) => recents.includes(s.id)).map((s) => ({ type: "sound" as const, data: s }));
    }
    if (filter === "combos") {
      const sorted = [...filteredCombos];
      sorted.sort((a, b) => {
        if (sort === "popular" || sort === "trending") {
          return b.play_count - a.play_count || b.created_at - a.created_at;
        }
        if (sort === "alpha") {
          return a.name.localeCompare(b.name);
        }
        return b.created_at - a.created_at;
      });
      return sorted.map((c) => ({ type: "combo" as const, data: c }));
    }

    // "all" — merge sounds and combos, sorted together
    const soundItems: FeedItem[] = sounds.map((s) => ({ type: "sound", data: s }));
    const comboItems: FeedItem[] = filteredCombos.map((c) => ({ type: "combo", data: c }));
    const merged = [...soundItems, ...comboItems];

    merged.sort((a, b) => {
      if (sort === "popular" || sort === "trending") {
        return b.data.play_count - a.data.play_count || b.data.created_at - a.data.created_at;
      }
      // newest, alphabetical, etc.
      if (sort === "alpha") {
        const nameA = a.type === "sound" ? a.data.title : a.data.name;
        const nameB = b.type === "sound" ? b.data.title : b.data.name;
        return nameA.localeCompare(nameB);
      }
      return b.data.created_at - a.data.created_at;
    });

    return merged;
  }, [sounds, filteredCombos, filter, sort, favorites, recents]);

  const visibleFeed = feed.slice(0, visibleCount);
  const hasMoreFeed = visibleCount < feed.length || !!cursor;
  const feedTotal = total + filteredCombos.length;

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
    // If we're running low on loaded sounds and there are more on the server, fetch them
    if (cursor && visibleCount + PAGE_SIZE >= sounds.length) {
      loadMore();
    }
  }, [cursor, visibleCount, sounds.length, loadMore]);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 border-b border-mist bg-ink/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-3 py-2 sm:px-4 sm:py-3">
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <img
              src="/brand/wizard-sounds-mark.svg"
              alt=""
              className="h-10 w-10 sm:hidden"
            />
            <img
              src="/brand/wizard-sounds-horizontal.svg"
              alt="wizard sounds"
              className="hidden h-12 sm:block"
            />
          </Link>

          <div className="relative flex min-w-0 flex-1 items-center justify-center px-2 sm:px-4">
            <NowPlayingBar />
            <HeaderQuote />
          </div>

          <div className="flex shrink-0 items-center gap-0.5 sm:gap-1">
            <Link
              to="/board"
              className="group/tip relative flex items-center gap-1.5 rounded-xl px-1.5 py-1.5 text-sm text-fog hover:bg-slate hover:text-parchment sm:px-2.5"
            >
              <Grid3X3 className="h-4 w-4" />
              <span className="hidden sm:inline">Board</span>
              <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-ink px-2 py-1 font-mono text-[10px] text-parchment opacity-0 shadow-lg transition-opacity duration-100 group-hover/tip:opacity-100 sm:hidden">Board</span>
            </Link>

            <button
              onClick={handleRandom}
              className="group/tip relative flex items-center gap-1.5 rounded-xl px-1.5 py-1.5 text-sm text-fog hover:bg-slate hover:text-parchment sm:px-2.5"
            >
              <Shuffle className="h-4 w-4" />
              <span className="hidden sm:inline">Random</span>
              <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-ink px-2 py-1 font-mono text-[10px] text-parchment opacity-0 shadow-lg transition-opacity duration-100 group-hover/tip:opacity-100 sm:hidden">Random</span>
            </button>

            <button
              onClick={() => setShowComboBuilder(true)}
              className="group/tip relative flex items-center gap-1.5 rounded-xl px-1.5 py-1.5 text-sm text-fog hover:bg-slate hover:text-parchment sm:px-2.5"
            >
              <Layers className="h-4 w-4" />
              <span className="hidden sm:inline">Combo</span>
              <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-ink px-2 py-1 font-mono text-[10px] text-parchment opacity-0 shadow-lg transition-opacity duration-100 group-hover/tip:opacity-100 sm:hidden">Create Combo</span>
            </button>

            <button
              onClick={() => setShowPlayback((v) => !v)}
              className={cn(
                "group/tip relative flex items-center gap-1.5 rounded-xl px-1.5 py-1.5 text-sm sm:px-2.5",
                playbackRate !== 1 || showPlayback
                  ? "bg-spark/15 text-spark"
                  : "text-fog hover:bg-slate",
              )}
            >
              <Volume2 className="h-4 w-4" />
              {playbackRate !== 1 && (
                <span className="pointer-events-none absolute -right-1 -top-1 rounded-full bg-spark px-1 font-mono text-[9px] font-semibold leading-tight text-ink">{playbackRate}×</span>
              )}
              <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-ink px-2 py-1 font-mono text-[10px] text-parchment opacity-0 shadow-lg transition-opacity duration-100 group-hover/tip:opacity-100">Playback</span>
            </button>

            {canInstall && (
              <button
                onClick={install}
                className="group/tip relative flex items-center gap-1.5 rounded-xl px-1.5 py-1.5 text-sm text-fog hover:bg-slate hover:text-parchment sm:px-2.5"
              >
                <Download className="h-4 w-4" />
                <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-ink px-2 py-1 font-mono text-[10px] text-parchment opacity-0 shadow-lg transition-opacity duration-100 group-hover/tip:opacity-100">Install</span>
              </button>
            )}

            <div className="mx-0.5 h-5 w-px bg-mist sm:mx-1" />

            <button
              onClick={() => setShowHelp(true)}
              className="group/tip relative flex items-center gap-1.5 rounded-xl px-1.5 py-1.5 text-sm text-fog hover:bg-slate hover:text-parchment sm:px-2.5"
            >
              <HelpCircle className="h-4 w-4" />
              <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-ink px-2 py-1 font-mono text-[10px] text-parchment opacity-0 shadow-lg transition-opacity duration-100 group-hover/tip:opacity-100">Help</span>
            </button>

            <button
              onClick={() => setShowStats(true)}
              className="group/tip relative flex items-center gap-1.5 rounded-xl px-1.5 py-1.5 text-sm text-fog hover:bg-slate hover:text-parchment sm:px-2.5"
            >
              <BarChart3 className="h-4 w-4" />
              <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-ink px-2 py-1 font-mono text-[10px] text-parchment opacity-0 shadow-lg transition-opacity duration-100 group-hover/tip:opacity-100">Stats</span>
            </button>

            <a
              href="/admin"
              className="group/tip relative flex items-center gap-1.5 rounded-xl px-1.5 py-1.5 text-sm text-fog hover:bg-slate hover:text-parchment sm:px-2.5"
            >
              <Wrench className="h-4 w-4" />
              <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-ink px-2 py-1 font-mono text-[10px] text-parchment opacity-0 shadow-lg transition-opacity duration-100 group-hover/tip:opacity-100">Admin</span>
            </a>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-3 py-3 sm:px-4 sm:py-6">
        {picks.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="mb-4 flex gap-2 overflow-x-auto pb-1 sm:mb-6 sm:grid sm:grid-cols-3 sm:gap-3 sm:overflow-visible sm:pb-0"
          >
            {picks.map((pick) => {
              const IconComponent = pick.icon === "trending" ? TrendingUp : pick.icon === "gem" ? Gem : Star;
              return (
                <button
                  key={pick.label}
                  onClick={() => handlePlay(pick.sound)}
                  className="group flex min-w-[200px] shrink-0 items-center gap-2 rounded-xl border border-spark/30 bg-spark/5 p-2 text-left transition-all hover:border-spark/60 hover:bg-spark/10 active:scale-[0.98] sm:min-w-0 sm:shrink sm:gap-3 sm:rounded-[18px] sm:p-4"
                >
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-spark/15 transition-colors group-hover:bg-spark/25 sm:h-9 sm:w-9">
                    <Play className="ml-0.5 h-3 w-3 text-spark sm:h-4 sm:w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 font-mono text-[10px] font-medium uppercase tracking-wider text-spark">
                      <IconComponent className="h-3 w-3" />
                      {pick.label}
                    </div>
                    <p className="mt-0.5 truncate font-display text-xs font-semibold tracking-display text-parchment sm:text-base">
                      {pick.sound.title}
                    </p>
                  </div>
                </button>
              );
            })}
          </motion.div>
        )}

        <div className="mb-3 sm:mb-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <div className="flex-1">
              <SearchBar value={query} onChange={setQuery} />
            </div>

            <div className="flex items-center gap-2">
              <span className="hidden font-mono text-[10px] uppercase tracking-wider text-fog sm:block">Sort by</span>
              <div className="relative">
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as typeof sort)}
                  className="appearance-none rounded-[18px] border border-mist bg-slate px-3 py-2 pr-8 text-sm text-parchment outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-fog" />
              </div>
            </div>
          </div>

          {!query && tags.length > 0 && (
            <div className="mt-2 flex items-center gap-1.5 overflow-x-auto">
              <span className="shrink-0 font-mono text-[9px] uppercase tracking-wider text-fog/50">Popular</span>
              {[...tags].sort((a, b) => b.sound_count - a.sound_count).slice(0, 6).map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => {
                    if (!selectedTags.includes(tag.slug)) toggleTag(tag.slug);
                    setShowTags(true);
                  }}
                  className="shrink-0 rounded-full bg-slate-raised/50 px-2 py-0.5 font-mono text-[10px] text-fog/70 transition-colors hover:bg-slate-raised hover:text-fog"
                >
                  {tag.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mb-3 space-y-2 sm:mb-4 sm:space-y-2.5">
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="shrink-0 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">Filter</span>
            <div className="-mx-3 flex items-center gap-1.5 overflow-x-auto px-3 sm:mx-0 sm:px-0">
              <button
                onClick={() => setFilter("all")}
                className={cn(
                  "shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-colors sm:px-4 sm:py-1.5 sm:text-sm",
                  filter === "all"
                    ? "bg-spark text-ink"
                    : "bg-slate-raised text-fog hover:text-parchment",
                )}
              >
                All ({feedTotal})
              </button>
              <button
                onClick={() => setFilter("favorites")}
                className={cn(
                  "flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors sm:gap-1.5 sm:px-4 sm:py-1.5 sm:text-sm",
                  filter === "favorites"
                    ? "bg-spark text-ink"
                    : "bg-slate-raised text-fog hover:text-parchment",
                )}
              >
                <Star className="h-3.5 w-3.5" /> Favorites
              </button>
              <button
                onClick={() => setFilter("recent")}
                className={cn(
                  "flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors sm:gap-1.5 sm:px-4 sm:py-1.5 sm:text-sm",
                  filter === "recent"
                    ? "bg-spark text-ink"
                    : "bg-slate-raised text-fog hover:text-parchment",
                )}
              >
                <Clock className="h-3.5 w-3.5" /> Recent
              </button>
              <button
                onClick={() => setFilter("combos")}
                className={cn(
                  "flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors sm:gap-1.5 sm:px-4 sm:py-1.5 sm:text-sm",
                  filter === "combos"
                    ? "bg-spark text-ink"
                    : "bg-slate-raised text-fog hover:text-parchment",
                )}
              >
                <Layers className="h-3.5 w-3.5" /> Combos ({filteredCombos.length})
              </button>
            </div>
          </div>

          {tags.length > 0 && (
            <div>
              <button
                onClick={() => setShowTags(!showTags)}
                className="flex items-center gap-1.5 font-mono text-[10px] font-medium uppercase tracking-wider text-fog transition-colors hover:text-parchment"
              >
                <Tag className="h-3 w-3" />
                Tags ({tags.length})
                {selectedTags.length > 0 && (
                  <span className="rounded-full bg-spark px-1.5 py-0.5 text-[9px] font-bold text-ink">{selectedTags.length}</span>
                )}
                <ChevronRight className={cn("h-3 w-3 transition-transform duration-150", showTags && "rotate-90")} />
              </button>
              {showTags && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  transition={{ duration: 0.15 }}
                  className="mt-2 flex flex-wrap gap-1.5 overflow-hidden rounded-[14px] border border-mist bg-slate/50 p-2.5 sm:gap-2 sm:p-3"
                >
                  {tags.map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => toggleTag(tag.slug)}
                      className={cn(
                        "rounded-full px-2.5 py-1 font-mono text-[10px] tracking-wider transition-colors sm:px-3 sm:text-[11px]",
                        selectedTags.includes(tag.slug)
                          ? "bg-spark text-ink"
                          : "bg-slate-raised text-fog hover:text-parchment",
                      )}
                    >
                      {tag.name} ({tag.sound_count})
                    </button>
                  ))}
                  {selectedTags.length > 0 && (
                    <button
                      onClick={() => selectedTags.forEach((t) => toggleTag(t))}
                      className="rounded-full px-2.5 py-1 font-mono text-[10px] tracking-wider text-ember transition-colors hover:bg-ember/10 sm:px-3 sm:text-[11px]"
                    >
                      Clear all
                    </button>
                  )}
                </motion.div>
              )}
            </div>
          )}
        </div>

        {loading && sounds.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-spark border-t-transparent" />
          </div>
        ) : visibleFeed.length === 0 ? (
          <div className="py-20 text-center text-fog">
            <p className="font-display text-lg tracking-display">
              {filter === "combos" ? "No combos yet" : "No sounds found"}
            </p>
            <p className="mt-1 text-sm">
              {filter === "combos"
                ? "Create the first combo by clicking the Combo button above"
                : query
                  ? "Try a different search term"
                  : "Check back soon for new sounds"}
            </p>
          </div>
        ) : (
          <>
            <LayoutGroup>
              <motion.div
                key={`${debouncedQuery}-${sort}-${selectedTags.join(",")}-${filter}`}
                className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-4"
                initial="hidden"
                animate="visible"
                variants={{
                  visible: { transition: { staggerChildren: 0.03 } },
                }}
              >
                {visibleFeed.map((item, i) =>
                  item.type === "sound" ? (
                    <motion.div
                      key={item.data.id}
                      layout="position"
                      variants={{
                        hidden: { opacity: 0, y: 12 },
                        visible: { opacity: 1, y: 0 },
                      }}
                      transition={{ duration: 0.2, layout: { duration: 0.25, ease: "easeInOut" } }}
                    >
                      <SoundCard
                        sound={item.data}
                        index={i}
                        isFavorite={isFavorite(item.data.id)}
                        onToggleFavorite={() => toggleFavorite(item.data.id)}
                        onPlay={() => { incrementPlayCount(item.data.id); addRecent(item.data.id); }}
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key={`combo-${item.data.id}`}
                      layout="position"
                      variants={{
                        hidden: { opacity: 0, y: 12 },
                        visible: { opacity: 1, y: 0 },
                      }}
                      transition={{ duration: 0.2, layout: { duration: 0.25, ease: "easeInOut" } }}
                    >
                      <ComboCard
                        combo={item.data}
                        isFavorite={isComboFavorite(item.data.id)}
                        onToggleFavorite={() => toggleComboFavorite(item.data.id)}
                        onPlay={() => handleComboPlayed(item.data.id)}
                      />
                    </motion.div>
                  ),
                )}
              </motion.div>
            </LayoutGroup>

            {hasMoreFeed && (
              <div className="mt-8 flex justify-center">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="rounded-[18px] border border-mist bg-slate-raised px-6 py-2 text-sm font-medium text-parchment hover:border-spark disabled:opacity-50"
                >
                  {loading ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <footer className="border-t border-mist py-6 sm:py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-3 px-3 sm:px-4">
          <div className="flex w-full flex-col items-center gap-3 sm:flex-row sm:justify-between">
            <img
              src="/brand/wizard-sounds-horizontal.svg"
              alt="wizard sounds"
              className="h-8 opacity-50"
            />
            <div className="flex items-center gap-3">
              <p className="hidden font-mono text-[11px] text-fog tracking-wider sm:block">
                Space to stop all, 1-9 for quick play
              </p>
              <span className="font-mono text-[10px] text-fog/50">v{__APP_VERSION__}</span>
            </div>
          </div>
        </div>
      </footer>

      {showPlayback && <PlaybackPanel onClose={() => setShowPlayback(false)} />}
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
      {showStats && <StatsModal onClose={() => setShowStats(false)} />}
      {showComboBuilder && (
        <ComboBuilderModal
          limitRemaining={comboLimitRemaining}
          onClose={() => setShowComboBuilder(false)}
          onCreated={(created) => {
            setShowComboBuilder(false);
            // Optimistically add to state — D1 read replicas may lag
            setCombos((prev) => [created, ...prev]);
            setComboLimitRemaining((prev) => Math.max(0, prev - 1));
          }}
        />
      )}
    </div>
  );
}
